import { html, unsafeCSS } from 'lit';
import { property } from 'lit/decorators.js';
import { DEFAULT_LABELS } from './morningstar-style-box.constants';
import { getGridPosition } from '../utils/morningstar.grid.utils';
import style from './morningstar-style-box.scss?inline';
import { PopBaseComponent } from '../../../base/pop-base-component';

/**
 * Morningstar Style Box Component
 *
 * A customizable grid component that displays investment style and market capitalization
 * relationships in a Morningstar-style format.
 *
 * @element pop-morningstar-style-box
 *
 * @property {string} marketCapLabel - Label for market capitalization axis
 * @property {string} investmentStyleLabel - Label for investment style axis
 * @property {CenterOfGravity} centerOfGravity - Position of the center of gravity
 * @property {string[]} investmentStyleLabels - Labels for investment style categories
 * @property {string[]} marketCapLabels - Labels for market cap categories
 * @property {string} asOfDate - Date for data as of label
 * @property {string} assetClass - Asset class label for the subheading
 *
 * @fires {CustomEvent} center-of-gravity-change - Fires when center of gravity changes
 *
 * @csspart grid - The main grid container
 * @csspart cell - Individual grid cells
 * @csspart label - Text labels
 *
 * @cssproperty --msb-cell-size - Size of grid cells
 * @cssproperty --msb-highlight-color - Color for highlighted cells
 * @cssproperty --msb-border-color - Color for grid borders
 * @cssproperty --msb-text-color - Color for text
 * @cssproperty --msb-background-color - Background color for cells
 */
export class MorningstarStyleBox extends PopBaseComponent {
  static get is() {
    // eslint-disable-next-line @typescript-eslint/consistent-type-assertions
    return 'pop-morningstar-style-box' as keyof HTMLElementTagNameMap;
  }

  static styles = [unsafeCSS(super.styles), unsafeCSS(style)];

  @property({ type: String })
  marketCapLabel: string = DEFAULT_LABELS.marketCap;

  @property({ type: String })
  investmentStyleLabel: string = DEFAULT_LABELS.investmentStyle;

  @property({ type: String, attribute: 'center-of-gravity' })
  centerOfGravity: `${string}-${string}` | null = null;

  @property({ type: Array })
  investmentStyleLabels: [string, string, string] =
    DEFAULT_LABELS.investmentStyleValues;

  @property({ type: Array })
  marketCapLabels: [string, string, string] = DEFAULT_LABELS.marketCapValues;

  @property({ type: String, attribute: 'as-of-date' })
  asOfDate: string | null = null;

  @property({ type: String, attribute: 'asset-class' })
  assetClass: string | null = null;

  /**
   * Computed property for current grid position based on center of gravity
   */
  private get gridData() {
    return getGridPosition(this.centerOfGravity);
  }

  render() {
    return html`
      <div class="morningstar-header__container">
        <pop-morningstar-style-box-heading
          .asOfDate=${this.asOfDate}
          .assetClass=${this.assetClass}
        ></pop-morningstar-style-box-heading>
      </div>
      <div aria-label="${this.getAccessibleText()}">
        <div class="morningstar-grid__container" aria-hidden="true">
          ${this.renderGridOverlay()}
          <div class="morningstar-grid__content">
            ${this.renderGridHeader()} ${this.renderGridBody()}
            ${this.renderGridFooter()}
          </div>
        </div>
      </div>
    `;
  }

  /**
   * Returns the accessible text for the grid
   */
  private getAccessibleText() {
    return `${this.marketCapLabel}: ${this.gridData.row}, ${this.investmentStyleLabel}: ${this.gridData.col}`;
  }

  /**
   * Renders the grid overlay content if provided
   */
  private renderGridOverlay() {
    return html`
      <div class="morningstar-grid__overlay">
        <slot name="overlay"></slot>
      </div>
    `;
  }

  /**
   * Renders the grid header section with labels
   */
  private renderGridHeader() {
    return html`
      <div
        class="morningstar-grid__label--vertical
      morningstar-grid__label
      morningstar-grid__area-left
      b-padding-block-start--micro--mini
      b-typography-sans-serif--caption--100
      b-margin-inline-end--micro--mini"
      >
        ${this.marketCapLabel}
      </div>
      <div
        class="b-typography-sans-serif--caption--100
        morningstar-grid__label
        morningstar-grid__area-top
        b-padding-block-start--micro--mini
        b-padding-block-end--micro--mini"
      >
        ${this.investmentStyleLabel}
      </div>
    `;
  }

  /**
   * Renders the main grid body with rows
   */
  private renderGridBody() {
    return html`<div class="morningstar-grid__rows__container">
      ${this.marketCapLabels.map(
        (label) =>
          html` <pop-morningstar-style-box-row
            .centerOfGravity=${this.centerOfGravity}
            .rowPosition=${label.toLowerCase()}
            .label=${label}
            .labels=${this.investmentStyleLabels}
            .boldRow=${this.gridData.row}
            .boldCol=${this.gridData.col}
          ></pop-morningstar-style-box-row>`,
      )}
    </div>`;
  }

  /**
   * Renders the grid footer with style labels
   */
  private renderGridFooter() {
    return html`
      <pop-morningstar-style-box-row
        class="morningstar-grid__area-footer b-padding-block-start--micro--mini"
        .labels=${this.investmentStyleLabels}
        .isLabelRow=${true}
        .boldCol=${this.gridData.col}
      ></pop-morningstar-style-box-row>
    `;
  }
}

declare global {
  interface HTMLElementTagNameMap {
    'pop-morningstar-style-box': MorningstarStyleBox;
  }
}

customElements.define(MorningstarStyleBox.is, MorningstarStyleBox);
